import React, { useMemo, useState } from "react";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { stringify } from "qs";
import { Card } from "main/javascripts/components/card/Card";
import { CardImage } from "main/javascripts/components/card/CardImage";
import { Cruise } from "main/javascripts/types/cruise";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  fontSize,
  fontWeight,
  letterSpacing,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  accentColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { CruiseRouteListModalDialog } from "./CruiseRouteListModalDialog";
import { SaleCruisePriceList } from "./SaleCruisePriceList";

export interface IProps {
  saleCruise: Cruise.SaleCruise;
}

export const SaleCruiseCard: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { saleCruise } = props;

  const { t } = useTranslation(["component", "label", "unit", "button"]);

  const vessel = saleCruise.vessel;
  const cruiseRoutes = saleCruise.cruiseRoutes || [];
  const salePrices = saleCruise.salePrices || [];

  const [isCruiseRoutesModalDisplayed, setIsCruiseRoutesModalDisplayed] =
    useState(false);

  const hideCruiseRoutesModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCruiseRoutesModalDisplayed(false);
  };

  const displayCruiseRoutesModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCruiseRoutesModalDisplayed(true);
  };

  const showPagePath = useMemo(() => {
    if (saleCruise.departureDate) {
      const params = {
        departure_date: saleCruise.departureDate,
        ...(salePrices[0] && { cabin_code: salePrices[0].vesselCabinCode }),
      };
      const paramsString = stringify(params);
      return `/cruises/${saleCruise.uniqueId}?${paramsString}`;
    }
    return `/cruises/${saleCruise.uniqueId}`;
  }, [saleCruise.departureDate]);
  const imageUrl: string = vessel.vesselImages?.[0]?.imageUrl;

  const displayDepartureDate = saleCruise.departureDate?.replace(/-/g, "/");
  const displayArrivalDate = saleCruise.arrivalDate?.replace(/-/g, "/");

  return (
    <Card overflowStyle="visible">
      <div css={innerSaleStyle}>
        <Link href={showPagePath} passHref>
          <a css={linkStyle} target="_blank">
            <div css={basicInfoStyle}>
              <div css={salePriceLabelStyle}>
                {t("component:cruise.salePrice")}
              </div>
              <div css={imageBlockStyle}>
                <div css={imageStyle}>
                  <CardImage url={imageUrl} />
                </div>
              </div>
              <div css={infoBlockStyle}>
                <div css={titleStyle}>{saleCruise.title}</div>
                <div css={dataBlockStyle}>
                  <div>
                    {t("cruise.vesselName")}: {vessel.name}
                  </div>
                  <div>
                    {t("cruise.schedule")}: {displayDepartureDate}{" "}
                    {t("component:common.rangeSeparator")} {displayArrivalDate}
                  </div>
                </div>
                <div css={dataBlockStyle}>
                  <div>
                    {t("cruise.departurePlace")}: {saleCruise.departurePortName}
                  </div>
                  {cruiseRoutes.length > 0 && (
                    <div>
                      {t("cruise.arrivalPlace")}:{" "}
                      {cruiseRoutes[cruiseRoutes.length - 1].title}
                    </div>
                  )}
                  <div css={showItineraryButtonBlockStyle}>
                    <div
                      css={showItineraryButtonStyle}
                      onClick={displayCruiseRoutesModal}
                    >
                      {t("button:cruise.showItinerary")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </Link>
        <SaleCruisePriceList
          salePrices={salePrices}
          uniqueId={saleCruise.uniqueId}
          departureDate={saleCruise.departureDate}
        />
      </div>
      <CruiseRouteListModalDialog
        displayed={isCruiseRoutesModalDisplayed}
        uniqueId={saleCruise.uniqueId}
        cruiseRoutes={saleCruise.cruiseRoutes}
        departureDate={saleCruise.departureDate}
        cabinCode={salePrices[0]?.vesselCabinCode}
        closeHandler={hideCruiseRoutesModal}
      />
    </Card>
  );
};

const innerSaleStyle = css`
  &::before {
    content: "";
    position: absolute;
    top: -1px;
    left: -1px;
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    border: 1px solid ${accentColor.primaryColor};
    box-sizing: border-box;
    border-radius: ${borderRadius.normal};
    pointer-events: none;
  }
`;
const basicInfoStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  flex: 1;
  cursor: pointer;
`;
const salePriceLabelStyle = css`
  position: absolute;
  top: ${space.atom2x};
  left: ${space.atom2x};
  background-color: ${textColor.secondaryAccentColor};
  color: #fff;
  font-size: ${fontSize.caption};
  font-weight: ${fontWeight.medium};
  line-height: ${lineHeight.caption2};
  letter-spacing: ${letterSpacing.text};
  padding: 0.125rem ${space.atom};
  ${moreThanBreakpoint("tablet")} {
    top: ${space.atom2_5x};
    left: ${space.atom2_5x};
  }
`;
const imageBlockStyle = css`
  width: 100%;
  box-sizing: border-box;
  padding: ${space.atom2x} ${space.atom2x} ${space.atom};
  ${moreThanBreakpoint("tablet")} {
    height: auto;
    width: 38%;
    padding: ${space.atom2_5x};
    padding-right: 0;
  }
`;
const imageStyle = css`
  aspect-ratio: 2 / 1;
  overflow: hidden;
  border-radius: ${borderRadius.normal};
  ${moreThanBreakpoint("tablet")} {
    aspect-ratio: 3 / 2;
  }
`;
const infoBlockStyle = css`
  width: 100%;
  box-sizing: border-box;
  padding: ${space.atom} ${space.atom};
  ${moreThanBreakpoint("tablet")} {
    width: 62%;
    padding: ${space.atom2x} ${space.atom1_5x};
  }
`;
const titleStyle = css`
  font-size: ${fontSize.headline};
  line-height: ${lineHeight.headline1};
  font-weight: ${fontWeight.medium};
  color: ${textColor.primaryDarkColor};
  padding: ${space.atom} ${space.atom};
`;
const dataBlockStyle = css`
  padding: ${space.atom} ${space.atom};
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  color: ${textColor.secondaryDarkColor};
`;
const showItineraryButtonBlockStyle = css`
  display: flex;
  justify-content: flex-start;
  padding: ${space.atom1_5x} 0 0;
`;
const showItineraryButtonStyle = css`
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  color: ${accentColor.primaryColor};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.5rem;
  transition: opacity 0.3s ease;
  &:hover {
    opacity: 0.8;
  }
`;
const linkStyle = css`
  text-decoration: none;
`;
